import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import type { AccountLoginUser } from '@automatick/shared-client-ng';
import { AuthService, type Session, type Token } from '@seech/auth-ng';
import {
  RealTimeCommunicationConnectionState,
  RealTimeCommunicationService,
  type RealTimeCommunicationEnvelope,
  type RealTimeCommunicationStatus,
} from '@seech/core-ng';
import {
  NotificationHubContract,
  UserNotificationEventName,
  UserNotificationStatus,
} from '@automatick/shared-ts';
import {
  type NotificationMessage,
  NotificationsClientService,
  type UserNotificationResponse,
} from '@automatick/shared-ng';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { buildRealtimeStartOptions } from '../shared/realtime/realtime-start-options';
import {
  buildIdleRealtimeConnectionHealth,
  buildRealtimeConnectionHealth,
  type RealtimeConnectionHealth,
} from '../shared/realtime/realtime-connection-health';
import { AppStateService } from './app-state.service';
import { CurrentDeviceContextService } from './current-device-context.service';
import { UserNotificationContractValidator } from './user-notification-contract-validator';
import {
  NotificationLifecyclePhase,
  type NotificationLifecyclePhase as NotificationLifecyclePhaseType,
  type UserNotificationQueryOptions,
} from './user-notification.models';

const NOTIFICATION_LIST_TAKE = 80;

export interface UserNotificationFeedState {
  readonly workspaceId: string | null;
  readonly notifications: readonly UserNotificationResponse[];
  readonly unreadCount: number;
  readonly actionRequiredCount: number;
  readonly isLoading: boolean;
  readonly phase: NotificationLifecyclePhaseType;
  readonly message: string;
  readonly isRealtimeConnected: boolean;
  readonly realtimeHealth: RealtimeConnectionHealth;
  readonly realtimeMessage: string;
}

@Injectable({ providedIn: 'root' })
export class UserNotificationFeedRuntime implements OnDestroy {
  private readonly appState = inject(AppStateService);
  private readonly notificationsClient = inject(NotificationsClientService);
  private readonly authService = inject(
    AuthService<AccountLoginUser, Session, Token>,
  );
  private readonly currentDeviceContext = inject(CurrentDeviceContextService);
  private readonly realtime = inject(RealTimeCommunicationService);
  private readonly zone = inject(NgZone);
  private readonly contracts = new UserNotificationContractValidator();
  private readonly destroy$ = new Subject<void>();
  private readonly receivedSubject = new Subject<UserNotificationResponse>();
  private readonly stateSubject =
    new BehaviorSubject<UserNotificationFeedState>({
      workspaceId: null,
      notifications: [],
      unreadCount: 0,
      actionRequiredCount: 0,
      isLoading: false,
      phase: NotificationLifecyclePhase.Idle,
      message: '',
      isRealtimeConnected: false,
      realtimeHealth: buildIdleRealtimeConnectionHealth(
        NotificationHubContract.ReceiveMethod,
      ),
      realtimeMessage: '',
    });
  private bootstrapStarted = false;
  private destroyed = false;
  private loadGeneration = 0;
  private currentQuery: UserNotificationQueryOptions = {
    includeDismissed: false,
    take: NOTIFICATION_LIST_TAKE,
  };

  readonly state$ = this.stateSubject.asObservable();
  readonly received$ = this.receivedSubject.asObservable();

  ngOnDestroy(): void {
    this.destroyed = true;
    this.loadGeneration += 1;
    this.destroy$.next();
    this.destroy$.complete();
    this.receivedSubject.complete();
  }

  bootstrap(): void {
    if (this.bootstrapStarted) {
      return;
    }
    this.bootstrapStarted = true;

    this.realtime.status$.pipe(takeUntil(this.destroy$)).subscribe((status) => {
      this.zone.run(() => this.applyRealtimeStatus(status));
    });
    this.realtime.events$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      this.zone.run(() => this.handleRealtimeEnvelope(event));
    });
    this.appState.state$
      .pipe(
        map((state) => state.activeWorkspaceId),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((workspaceId) => this.activateWorkspace(workspaceId));
  }

  currentWorkspaceId(): string | null {
    return this.stateSubject.value.workspaceId;
  }

  refresh(): void {
    this.load(this.currentQuery);
  }

  load(options: UserNotificationQueryOptions = this.currentQuery): void {
    const workspaceId = this.currentWorkspaceId();
    if (!workspaceId || this.destroyed) {
      return;
    }
    const generation = this.loadGeneration + 1;
    this.loadGeneration = generation;
    this.currentQuery = this.normalizeQuery(options);
    this.patch({
      isLoading: true,
      phase: NotificationLifecyclePhase.Loading,
      message: '',
    });

    this.notificationsClient
      .queryUserNotifications({
        body: {
          statuses: [...(this.currentQuery.statuses ?? [])],
          categories: [...(this.currentQuery.categories ?? [])],
          severities: [...(this.currentQuery.severities ?? [])],
          actionRequiredOnly: this.currentQuery.actionRequiredOnly,
          includeDismissed: this.currentQuery.includeDismissed,
          take: this.currentQuery.take,
        },
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (notifications) => {
          if (!this.isLoadCurrent(generation, workspaceId)) {
            return;
          }
          const valid = notifications.filter(
            (notification) =>
              this.contracts.isNotification(notification) &&
              notification.workspaceId === workspaceId &&
              this.matchesCurrentQuery(notification),
          );
          this.setNotifications(valid);
          this.patch({
            isLoading: false,
            phase:
              valid.length !== notifications.length
                ? NotificationLifecyclePhase.Partial
                : valid.length > 0
                  ? NotificationLifecyclePhase.Ready
                  : NotificationLifecyclePhase.Empty,
          });
        },
        error: () => {
          if (!this.isLoadCurrent(generation, workspaceId)) {
            return;
          }
          this.patch({
            isLoading: false,
            phase: NotificationLifecyclePhase.Error,
            message: 'Notifications are temporarily unavailable.',
          });
        },
      });
  }

  upsert(notification: UserNotificationResponse): void {
    const notificationId = notification.notificationId?.trim();
    if (
      !notificationId ||
      !this.contracts.isNotification(notification) ||
      notification.workspaceId !== this.currentWorkspaceId()
    ) {
      return;
    }

    const existing = this.stateSubject.value.notifications.filter(
      (item) => item.notificationId !== notificationId,
    );
    this.setNotifications(
      this.matchesCurrentQuery(notification)
        ? [notification, ...existing]
        : existing,
    );
  }

  private activateWorkspace(workspaceId: string | null): void {
    if (this.currentWorkspaceId() === workspaceId) {
      return;
    }
    this.loadGeneration += 1;
    this.stateSubject.next({
      ...this.stateSubject.value,
      workspaceId,
      notifications: [],
      unreadCount: 0,
      actionRequiredCount: 0,
      isLoading: false,
      phase: NotificationLifecyclePhase.Idle,
      message: '',
      realtimeMessage: '',
      ...(!workspaceId
        ? {
            isRealtimeConnected: false,
            realtimeHealth: buildIdleRealtimeConnectionHealth(
              NotificationHubContract.ReceiveMethod,
            ),
          }
        : {}),
    });
    if (!workspaceId) {
      return;
    }
    this.refresh();
    void this.ensureRealtimeConnectionStartedAsync(workspaceId);
  }

  private async ensureRealtimeConnectionStartedAsync(
    workspaceId: string,
  ): Promise<void> {
    try {
      await this.realtime.start(
        buildRealtimeStartOptions({
          currentDeviceContext: this.currentDeviceContext.snapshot(),
          accessTokenFactory: () => this.getAccessTokenAsync(),
        }),
      );
    } catch {
      if (this.destroyed || this.currentWorkspaceId() !== workspaceId) {
        return;
      }
      this.patch({
        isRealtimeConnected: false,
        realtimeMessage: 'Realtime notifications are temporarily unavailable.',
      });
    }
  }

  private applyRealtimeStatus(status: RealTimeCommunicationStatus): void {
    const realtimeHealth = buildRealtimeConnectionHealth(status);
    const isConnected =
      status.state === RealTimeCommunicationConnectionState.Connected;
    this.patch({
      isRealtimeConnected: isConnected,
      realtimeHealth,
      realtimeMessage:
        !isConnected &&
        status.state === RealTimeCommunicationConnectionState.Failed &&
        status.lastError
          ? 'Live notification updates are temporarily unavailable.'
          : '',
    });
  }

  private handleRealtimeEnvelope(
    envelope: RealTimeCommunicationEnvelope<unknown, unknown>,
  ): void {
    if (!this.isNotificationMessage(envelope.payload)) {
      return;
    }
    const message = envelope.payload;
    if (!this.isUserNotificationEventName(message.eventName)) {
      return;
    }
    const notification = message.userNotification;
    if (
      !this.contracts.isNotification(notification) ||
      notification.workspaceId !== this.currentWorkspaceId()
    ) {
      return;
    }
    this.upsert(notification);
    this.receivedSubject.next(notification);
  }

  private isNotificationMessage(value: unknown): value is NotificationMessage {
    return (
      this.isRecord(value) &&
      typeof value['eventName'] === 'string' &&
      this.isRecord(value['userNotification'])
    );
  }

  private isUserNotificationEventName(
    value: string | null | undefined,
  ): value is UserNotificationEventName {
    return (
      value === UserNotificationEventName.Created ||
      value === UserNotificationEventName.Updated
    );
  }

  private matchesCurrentQuery(notification: UserNotificationResponse): boolean {
    const query = this.currentQuery;
    return (
      (query.includeDismissed ||
        notification.status !== UserNotificationStatus.Dismissed) &&
      (!query.statuses?.length ||
        query.statuses.some((status) => status === notification.status)) &&
      (!query.categories?.length ||
        query.categories.some(
          (category) => category === notification.category,
        )) &&
      (!query.severities?.length ||
        query.severities.some(
          (severity) => severity === notification.severity,
        )) &&
      (!query.actionRequiredOnly ||
        notification.status === UserNotificationStatus.ActionRequired)
    );
  }

  private setNotifications(
    notifications: readonly UserNotificationResponse[],
  ): void {
    const ordered = [...notifications].sort(
      (left, right) =>
        this.toTimestamp(right.createdOn) - this.toTimestamp(left.createdOn),
    );
    this.patch({
      notifications: ordered,
      unreadCount: ordered.filter(
        (item) =>
          item.status === UserNotificationStatus.Unread ||
          item.status === UserNotificationStatus.ActionRequired,
      ).length,
      actionRequiredCount: ordered.filter(
        (item) => item.status === UserNotificationStatus.ActionRequired,
      ).length,
    });
  }

  private normalizeQuery(
    options: UserNotificationQueryOptions,
  ): UserNotificationQueryOptions {
    return {
      statuses: options.statuses,
      categories: options.categories,
      severities: options.severities,
      actionRequiredOnly: options.actionRequiredOnly ?? false,
      includeDismissed: options.includeDismissed ?? false,
      take: options.take ?? NOTIFICATION_LIST_TAKE,
    };
  }

  private isLoadCurrent(generation: number, workspaceId: string): boolean {
    return (
      !this.destroyed &&
      this.loadGeneration === generation &&
      this.currentWorkspaceId() === workspaceId
    );
  }

  private patch(patch: Partial<UserNotificationFeedState>): void {
    if (!this.destroyed) {
      this.stateSubject.next({ ...this.stateSubject.value, ...patch });
    }
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  private toTimestamp(value: string | null | undefined): number {
    if (!value) {
      return 0;
    }
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
  }

  private async getAccessTokenAsync(): Promise<string> {
    const currentToken = this.authService.getCurrentToken();
    if (currentToken?.access_token && this.authService.isLoggedInNotExpired()) {
      return currentToken.access_token;
    }
    const refreshToken = currentToken?.refresh_token;
    if (!refreshToken) {
      return currentToken?.access_token ?? '';
    }
    try {
      const refreshedToken = await firstValueFrom(
        this.authService.newAccessToken(refreshToken),
      );
      return refreshedToken?.access_token ?? currentToken?.access_token ?? '';
    } catch {
      return currentToken?.access_token ?? '';
    }
  }
}
